import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { trackPageView, setGAUser } from './services/analytics';
import { useAuth } from './context/AuthContext';
import CookieConsentBanner from './components/common/CookieConsentBanner/CookieConsentBanner';
import HowItWorksPopup from './components/common/HowItWorksPopup/HowItWorksPopup';

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function UserTracker() {
  const { currentUser } = useAuth();
  useEffect(() => {
    if (currentUser?._id) setGAUser(currentUser._id);
  }, [currentUser]);
  return null;
}

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    const key = `scrollPos:${location.pathname}${location.search}`;
    if (!sessionStorage.getItem(key)) {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.search]);
  return null;
}

// Import components
import Header from './components/common/Header/Header';
import Footer from './components/common/Footer/Footer';
import NotificationBanner from './components/common/NotificationBanner/NotificationBanner';
import CountdownBar from './components/common/CountdownBar/CountdownBar';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';

// Import pages — HomePage is eager (LCP element lives here); all others are lazy
import HomePage from './pages/HomePage/HomePage';
const CategoryPage = lazy(() => import('./pages/CategoryPage/CategoryPage'));
const ProductPage = lazy(() => import('./pages/ProductPage/ProductPage'));
const CartPage = lazy(() => import('./pages/CartPage/CartPage'));
const MultiStepCheckout = lazy(() => import('./pages/MultiStepCheckout/MultiStepCheckout'));
const LoginPage = lazy(() => import('./pages/LoginPage/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage/ResetPasswordPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage/EmailVerificationPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage/ProfilePage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage/UnauthorizedPage'));
const OrderSuccessPage = lazy(() => import('./pages/OrderSuccessPage/OrderSuccessPage'));
const OrderHistoryPage = lazy(() => import('./pages/OrderHistoryPage/OrderHistoryPage'));
const OrderDetailPage = lazy(() => import('./pages/OrderDetailPage/OrderDetailPage'));

// Import request system components
const RequestDashboard = lazy(() => import('./components/requests/RequestDashboard'));
const RequestDetailView = lazy(() => import('./components/requests/RequestDetailView'));
const RequestItemPage = lazy(() => import('./pages/RequestItemPage/RequestItemPage'));
const AdminRequestsDashboard = lazy(() => import('./components/requests/AdminRequestsDashboard'));
// import InventoryTrackingComponent from './components/common/InventoryTracking/InventoryTracking';

// Import context providers
import { CartProvider } from './context/CartContext';
import { AuthProvider } from './context/AuthContext';
import { CartAnimationProvider } from './context/CartAnimationContext';
import './context/CartAnimation.css';
const InventoryDashboard = lazy(() => import('./components/common/InventoryDashboard/InventoryDashboard'));
const ProductManagement = lazy(() => import('./components/common/ProductManagement/ProductManagement'));
const AdminOrdersPage = lazy(() => import('./pages/AdminOrdersPage/AdminOrdersPage'));
const AdminCollectPage = lazy(() => import('./pages/AdminCollectPage/AdminCollectPage'));
const AdminFeaturedLootPage = lazy(() => import('./pages/AdminFeaturedLootPage/AdminFeaturedLootPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage/UserManagementPage'));
const MonthlyPinsPage = lazy(() => import('./pages/MonthlyPinsPage/MonthlyPinsPage'));
const MysteryPinsPage = lazy(() => import('./pages/MysteryPinsPage/MysteryPinsPage'));
const FAQsPage = lazy(() => import('./pages/FAQsPage/FAQsPage'));
const AboutPage = lazy(() => import('./pages/AboutPage/AboutPage'));
const GeneralPoliciesPage = lazy(() => import('./pages/GeneralPoliciesPage/GeneralPoliciesPage'));
const SubscriptionPoliciesPage = lazy(() => import('./pages/SubscriptionPoliciesPage/SubscriptionPoliciesPage'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage/SubscriptionPage'));
const SubscriptionSuccessPage = lazy(() => import('./pages/SubscriptionSuccessPage/SubscriptionSuccessPage'));
const AdminSubscriptionFulfillmentPage = lazy(() => import('./pages/AdminSubscriptionFulfillmentPage/AdminSubscriptionFulfillmentPage'));
const AdminSettingsPage = lazy(() => import('./pages/AdminSettingsPage/AdminSettingsPage'));
const VaultPage = lazy(() => import('./pages/VaultPage/VaultPage'));
const ContactPage = lazy(() => import('./pages/ContactPage/ContactPage'));

const ProductRedirect = () => {
  const { productId } = useParams();
  return <Navigate to={`/product/${productId}`} replace />;
};

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <CartProvider>
      <CartAnimationProvider>
        <Router>
          <div className="app">
            <PageViewTracker />
            <UserTracker />
            <ScrollToTop />
<Header />
            <CountdownBar />
            <main className="main-content">
              <Suspense fallback={null}>
              <Routes>
                {/* Auth-only public routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password/:resetToken" element={<ResetPasswordPage />} />
                <Route path="/verify-email/:token" element={<EmailVerificationPage />} />
                <Route path="/unauthorized" element={<UnauthorizedPage />} />

                {/* Public browsing routes */}
                <Route path="/" element={<HomePage />} />
                <Route path="/category/:categoryName" element={<CategoryPage />} />
                <Route path="/product/:productId" element={<ProductPage />} />
                <Route path="/products/:productId" element={<ProductRedirect />} />
                <Route path="/faq" element={<FAQsPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/general-policies" element={<GeneralPoliciesPage />} />
                <Route path="/subscription-policies" element={<SubscriptionPoliciesPage />} />

                {/* Protected routes - require authentication */}
                <Route path="/subscriptions/monthly-pins" element={<ProtectedRoute><MonthlyPinsPage /></ProtectedRoute>} />
                <Route path="/subscriptions" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
                <Route path="/subscriptions/mystery-pins" element={<ProtectedRoute><MysteryPinsPage /></ProtectedRoute>} />
                <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccessPage /></ProtectedRoute>} />
                <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
                <Route path="/checkout" element={<ProtectedRoute><MultiStepCheckout /></ProtectedRoute>} />
                <Route path="/order-success" element={<ProtectedRoute><OrderSuccessPage /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
                <Route path="/orders" element={<ProtectedRoute><OrderHistoryPage /></ProtectedRoute>} />
                <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetailPage /></ProtectedRoute>} />
                <Route path="/my-requests" element={<ProtectedRoute><RequestDashboard /></ProtectedRoute>} />
                <Route path="/request/:requestId" element={<ProtectedRoute><RequestDetailView /></ProtectedRoute>} />
                <Route path="/request-item" element={<ProtectedRoute><RequestItemPage /></ProtectedRoute>} />
                <Route path="/vault" element={<ProtectedRoute><VaultPage /></ProtectedRoute>} />
                
                {/* Protected routes for admin users */}
                <Route path="/admin/requests" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminRequestsDashboard />
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/orders" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminOrdersPage />
                  </ProtectedRoute>
                } />

                <Route path="/admin/collect" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminCollectPage />
                  </ProtectedRoute>
                } />

                <Route path="/admin/inventory" element={
                  <ProtectedRoute requiredRole="admin">
                    <InventoryDashboard />
                  </ProtectedRoute>
                } />

                <Route path="/admin/products" element={
                  <ProtectedRoute requiredRole="admin">
                    <ProductManagement />
                  </ProtectedRoute>
                } />

                <Route path="/admin/featured-loot" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminFeaturedLootPage />
                  </ProtectedRoute>
                } />

                <Route path="/admin/subscriptions" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminSubscriptionFulfillmentPage />
                  </ProtectedRoute>
                } />

                <Route path="/admin/settings" element={
                  <ProtectedRoute requiredRole="admin">
                    <AdminSettingsPage />
                  </ProtectedRoute>
                } />

                <Route path="/admin/users" element={
                  <ProtectedRoute requiredRole="admin">
                    <UserManagementPage />
                  </ProtectedRoute>
                } />
              </Routes>
              </Suspense>
            </main>
            <Footer />
            <HowItWorksPopup />
            <CookieConsentBanner />
          </div>
        </Router>
      </CartAnimationProvider>
      </CartProvider>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;